import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ShieldCheck, RefreshCw, Users, Loader2 } from "lucide-react";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";
import { fmtDate } from "../../lib/utils";
import { PageHeader } from "../../components/app/Common";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "active", label: "Active" },
  { key: "rejected", label: "Rejected" },
];

/**
 * Master Admin Dashboard — visible only when the current user has
 * `is_master_admin = true`. Lists every tenant company grouped by status.
 * Approve/Reject buttons flip the status; the target company is notified in
 * real time via SSE so pending users on the Waiting Screen jump to /app
 * without a refresh.
 */
export default function MasterCompanies() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({}); // { [companyId]: "approve" | "reject" }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/master/companies?status=${status}`);
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load companies");
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const act = async (row, action) => {
    setActing((s) => ({ ...s, [row.id]: action }));
    try {
      await api.post(`/master/companies/${row.id}/${action}`);
      toast.success(action === "approve" ? `Approved · ${row.name}` : `Rejected · ${row.name}`);
      setRows((r) => r.filter((x) => x.id !== row.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setActing((s) => { const { [row.id]: _, ...rest } = s; return rest; });
    }
  };

  return (
    <div className="space-y-6" data-testid="master-dashboard">
      <PageHeader
        title="Company approvals"
        subtitle="Every new company signup lands here for manual approval before it can use SiteSync."
      />

      <Tabs value={status} onValueChange={setStatus}>
        <div className="flex items-center gap-3">
          <TabsList className="rounded-sm">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`master-tab-${t.key}`}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <Button size="sm" variant="outline" className="rounded-sm border-border" onClick={load} disabled={loading} data-testid="master-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <div className="ml-auto text-xs text-muted-foreground font-mono">
            {rows.length} row{rows.length === 1 ? "" : "s"}
          </div>
        </div>

        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-4">
            {loading ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2 py-8 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : rows.length === 0 ? (
              <div className="border border-dashed border-border rounded-sm p-10 text-center text-sm text-muted-foreground" data-testid={`master-empty-${t.key}`}>
                No {t.label.toLowerCase()} companies.
              </div>
            ) : (
              <div className="space-y-2" data-testid={`master-list-${t.key}`}>
                {rows.map((row) => (
                  <CompanyRow
                    key={row.id}
                    row={row}
                    status={status}
                    busy={acting[row.id]}
                    onApprove={() => act(row, "approve")}
                    onReject={() => act(row, "reject")}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function CompanyRow({ row, status, busy, onApprove, onReject }) {
  return (
    <div className="border border-border rounded-sm p-4 bg-card flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`master-row-${row.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base font-semibold truncate">{row.name}</span>
          <StatusBadge status={row.status} />
          {row.is_system && <Badge variant="outline" className="rounded-sm text-[10px] font-mono">SYSTEM</Badge>}
          <span className="text-xs text-muted-foreground font-mono flex items-center gap-1">
            <Users className="h-3 w-3" /> {row.user_count || 0}
          </span>
        </div>
        <div className="text-xs text-muted-foreground font-mono mt-1 truncate">
          {row.primary_admin?.name ? `${row.primary_admin.name} · ` : ""}
          {row.primary_admin?.email || "no admin"}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          Submitted {fmtDate(row.created_at)}
          {row.reviewed_at && ` · reviewed ${fmtDate(row.reviewed_at)} by ${row.reviewed_by}`}
        </div>
      </div>
      {status === "pending" && (
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10" onClick={onReject} disabled={!!busy} data-testid={`master-reject-${row.id}`}>
            {busy === "reject"
              ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Rejecting…</>
              : <><XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject</>}
          </Button>
          <Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90" onClick={onApprove} disabled={!!busy} data-testid={`master-approve-${row.id}`}>
            {busy === "approve"
              ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Approving…</>
              : <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Approve</>}
          </Button>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    active: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30",
    rejected: "bg-destructive/15 text-destructive border-destructive/30",
  };
  return (
    <Badge variant="outline" className={`rounded-sm font-mono uppercase tracking-widest text-[10px] ${map[status] || ""}`} data-testid={`master-status-${status}`}>
      {status}
    </Badge>
  );
}
